"use client";

import useSWR from "swr";
import { useAuth } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";
import type {
  Customer,
  Driver,
  Load,
  Order,
  Vehicle,
} from "@/lib/api-types";

export type SearchEntity =
  | "all"
  | "orders"
  | "loads"
  | "customers"
  | "drivers"
  | "vehicles";

// SearchResults mirrors the backend searchResponse struct. `source`
// tells us which backend served the response — elasticsearch (default)
// or postgres (fallback when ES is down). Surface it in dev tools but
// keep it invisible to end users by default; the fallback is supposed
// to be silent.
export interface SearchResults {
  orders: Order[];
  loads: Load[];
  customers: Customer[];
  drivers: Driver[];
  vehicles: Vehicle[];
  order_total: number;
  load_total: number;
  customer_total: number;
  driver_total: number;
  vehicle_total: number;
  source?: "elasticsearch" | "postgres";
}

const empty: SearchResults = {
  orders: [],
  loads: [],
  customers: [],
  drivers: [],
  vehicles: [],
  order_total: 0,
  load_total: 0,
  customer_total: 0,
  driver_total: 0,
  vehicle_total: 0,
};

export interface UseSearchOptions {
  /**
   * Scope the search to a single entity type. Defaults to "all" which
   * fans out across orders, loads, customers, drivers, and vehicles
   * (the sidebar GlobalSearch usage). Per-page search inputs
   * (/orders?q=..., /loads?q=...) should pass the matching entity so
   * the backend doesn't pay for fan-out it'll throw away.
   */
  entity?: SearchEntity;
  /** Result limit per entity. Defaults to 10. */
  limit?: number;
}

export function useSearch(query: string, options: UseSearchOptions = {}) {
  const { entity = "all", limit = 10 } = options;
  const { getToken } = useAuth();

  const key =
    query.length >= 2
      ? `/api/v1/search?q=${encodeURIComponent(query)}&limit=${limit}&entity=${entity}`
      : null;

  const { data, error, isLoading } = useSWR<SearchResults>(
    key,
    createFetcher(getToken),
    { dedupingInterval: 300 },
  );

  return {
    results: data ?? empty,
    isLoading,
    error,
    /** True when the response was served by the Postgres fallback path. */
    isDegraded: data?.source === "postgres",
  };
}
